import { Grid } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import {
  CardComponent,
  ConfirmationPopup,
  ContractRenewalCard,
  DatePicker,
  Delete,
  DeleteBadge,
  Edit,
  PageTitleAndSubTitle,
} from '@HomaHealth/ui-component-library/lib/_AIQ-components';
import { observer } from 'mobx-react';
import React, { useEffect, useState } from 'react';
import { RECOMMENDED_APPS } from '../../core-utils/messages/RecommendedAppConstants';
import { patientAccessTheme } from '@HomaHealth/ui-component-library/lib/theme/theme';
import {
  Button,
  Typography,
} from '@HomaHealth/ui-component-library/lib/_patient-access-components';
import Loader from '../../_components/molecules/Loader';
import NoAnnualData from '../../_components/atoms/Icons/NoAnnualData';
import PopUpAddNewAgreement from '../../_components/molecules/PopUpAddNewAgreement';
import { scrollBarStyles } from '../../core-utils/messages/CommonConstants';
import useSnackbar from '../../core-utils/custom-hooks/Snackbar';
import { getServiceAgreements } from '../../core-utils/axios/compliance-assurance/contracts';
import properties from '../../core-utils/properties';
import axios from 'axios';
import { useStores } from '../../stores';
import Snackbar from '../../_components/atoms/Snackbar';
import { formatDateToDDMMMMYYYY } from '../../core-utils/helper-utils/helper';
import { useNavigate } from 'react-router-dom';
import { RENEWAL_DETAILS } from '../../core-utils/routes';

// Inside AnnualRenewalProcess.tsx

export interface ContractOption {
  label: string;
  value: string;
  contractData: any;
}

const useStyles = makeStyles()({
  root: {
    display: 'flex',
    flexDirection: 'column',
    padding: '0px 60px',
  },
  title: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flexWrap: 'wrap',
  },
  titleAndSubtitle: {
    padding: '32px 0px',
    width: '1240px',
  },
  hr: {
    border: `1px solid ${patientAccessTheme?.palette?.grey[300]}`,
    margin: '18px 0px',
  },
  cardBody: {
    marginTop: '10px',
    height: 'auto',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  searchContainer: {
    marginBottom: '16px',
    padding: '0 8px',
    flexShrink: 0,
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: '16px',
  },
  searchField: {
    flex: 1,
    minWidth: 0,
  },
  scrollableCardsContainer: {
    flex: 1,
    overflowY: 'auto',
    overflowX: 'auto',
    paddingRight: '8px',
    padding: '8px',
    scrollbarWidth: 'none',
    '-ms-overflow-style': 'none',
    '&::-webkit-scrollbar': {
      display: 'none',
    },
    ...scrollBarStyles,
  },
  cardsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, minmax(300px, 1fr))',
    gap: '14px',
    padding: '8px 0',
    minWidth: 0,
  },
  noDataContainer: {
    display: 'flex',
    justifyContent: 'center',
    flexDirection: 'column',
    alignItems: 'center',
    height: '100%',
    minHeight: '300px',
  },
  cardTitleContainer: {
    width: '1200px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
});

const AnnualRenewalProcess = observer(() => {
  const { classes } = useStyles();
  const message = RECOMMENDED_APPS?.ANNUAL_RENEWAL_PROCESS;
  const employerGroupStore = useStores()?.employerGroupStore;
  const eg_nid = employerGroupStore?.employerGroupResponseData?.eg_nid[0];
  const loginStore = useStores()?.loginPageStore;
  const email = loginStore?.email;
  const { snackbarState, openSnackbar, handleCloseSnackBar } = useSnackbar();
  const navigate = useNavigate();
  const menuItems = [
    {
      label: 'Edit',
      icon: <Edit fill={patientAccessTheme.palette.status.icon} />,
    },
    {
      label: 'Delete',
      icon: <Delete color={patientAccessTheme.palette.status.icon} />,
    },
  ];

  // States declared
  const [contracts, setContracts] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [mainLoading, setMainLoading] = useState<boolean>(true);
  const [open, setOpen] = useState<boolean>(false);
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [vendorTypes, setVendorTypes] = useState<string[]>([]);
  const [selectedVendorType, setSelectedVendorType] = useState<string>('');
  const [currentAgreementOptions, setCurrentAgreementOptions] = useState<
    ContractOption[]
  >([]);
  const [selectedContractId, setSelectedContractId] = useState<string>('');

  const [
    selectedCurrentAgreement,
    setSelectedCurrentAgreement,
  ] = useState<string>('');
  const [selectedRenewalId, setSelectedRenewalId] = useState<string>('');
  const [renewalDate, setRenewalDate] = useState<string>('');
  const [expectedReleaseDate, setExpectedReleaseDate] = useState<string>('');
  //selectedRenewalPeriod format 'January 2025'
  const [selectedRenewalPeriod, setSelectedRenewalPeriod] = useState<string>(
    '',
  );
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);

  //Functions
  const formatToISO = (ddmmyyyy: string): string => {
    if (ddmmyyyy && /^\d{4}-\d{2}-\d{2}$/.test(ddmmyyyy)) {
      return ddmmyyyy;
    }
    const [day, month, year] = ddmmyyyy?.split('/');
    return `${year}-${month?.padStart(2, '0')}-${day?.padStart(2, '0')}`;
  };

  const handleSubmit = async () => {
    try {
      if (!isEdit) {
        if (!selectedVendorType || !selectedContractId || !renewalDate) {
          openSnackbar('Please fill all required fields', 'warning', 3000);
          return;
        }

        const [monthName, yearStr] = selectedRenewalPeriod?.trim()?.split(' ');
        const year = parseInt(yearStr);

        if (!monthName || isNaN(year)) {
          openSnackbar('Invalid renewal period selected', 'error', 3000);
          return;
        }

        const payload = {
          eg_nid: eg_nid,
          contract_id: selectedContractId,
          year: year,
          month: monthName,
          renewal_date: formatToISO(renewalDate),
          expected_release_date:
            expectedReleaseDate === ''
              ? null
              : formatToISO(expectedReleaseDate),

          status: 'Not Started',
          createdBy: email,
          service_agreement_name: selectedVendorType,
        };

        await addRenewalProcess(payload);
        openSnackbar('Renewal process added successfully!', 'success', 3000);
      } else {
        await editRenewalProcess(selectedRenewalId, {
          contract_id: selectedContractId,
          renewal_date: formatToISO(renewalDate),
          expected_release_date: formatToISO(expectedReleaseDate),
        });
      }
    } catch (error) {
      openSnackbar(
        isEdit
          ? 'Failed to edit renewal process'
          : 'Failed to add renewal process',
        'error',
        3000,
      );
    } finally {
      setIsEdit(false);
      setOpen(false);
      fetchRenewals();
      setSelectedVendorType('');
      setSelectedContractId('');
      setRenewalDate('');
      setExpectedReleaseDate('');
    }
  };

  const handleStartRenewal = (contract: any) => {
    navigate(RENEWAL_DETAILS, {
      state: {
        renewalId: contract?._id,
        serviceAgreement: contract?.agreementName,
      },
    });
  };

  const handleMenuItemClick = (menu: string, data: any) => {
    setSelectedRenewalId(data?._id);

    switch (menu) {
      case 'Edit':
        setOpen(true);
        setIsEdit(true);
        setSelectedCurrentAgreement(data?.health_care_partner_name);
        setSelectedContractId(data?.contract_id);
        setSelectedVendorType(data?.service_agreement_name);
        setRenewalDate(data?.renewal_date);
        setExpectedReleaseDate(data?.expected_release_date);
        break;
      case 'Delete':
        setIsDeleteModalOpen(true);
        break;
      default:
        break;
    }
  };

  const handleDeleteRenewal = async () => {
    try {
      await deleteRenewalProcess(selectedRenewalId);
      openSnackbar('Renewal process deleted successfully!', 'success', 3000);
      fetchRenewals();
    } catch (error) {
      openSnackbar('Failed to delete renewal process', 'error', 3000);
    } finally {
      setIsDeleteModalOpen(false);
    }
  };

  // Apis
  const getDefaultRenewalMonth = async () => {
    try {
      const response = await axios?.post(
        properties?.aiqBaseUrl +
          properties?.dataCatalogueBaseURL +
          properties?.getOrganizationInfo,
        {
          eg_nid: eg_nid,
        },
      );
      const month =
        response?.data?.data?.employer_plan?.renewal_month || 'January';

      const year = new Date().getFullYear() + 1;
      const defaultPeriod = `${month} ${year}`;
      setSelectedRenewalPeriod(defaultPeriod);
    } catch (error) {
      openSnackbar('Failed to load renewal date', 'error', 5000);
    } finally {
      setMainLoading(false);
    }
  };

  const fetchServiceAgreements = async () => {
    try {
      const vendorTypes = await getServiceAgreements(openSnackbar);
      setVendorTypes(
        vendorTypes?.map((item: any) => item?.service_agreement_name) || [],
      );
    } catch (error) {}
  };

  const getContracts = async () => {
    if (!selectedVendorType || !eg_nid) {
      setCurrentAgreementOptions([]);
      return;
    }

    try {
      const response = await axios?.post(
        properties?.aiqBaseUrl +
          properties?.contractBaseUrl +
          properties?.getContracts,
        {
          eg_nid: eg_nid,
          service_agreement_name: selectedVendorType,
        },
      );

      const contractsData = response?.data?.data || [];

      const options: ContractOption[] = contractsData?.map((contract: any) => {
        const partnerName =
          contract?.health_care_partner_name?.trim() || 'Unknown Partner';
        const rawDate = contract?.effective_date?.trim() || '';

        let formattedDate = '';
        if (rawDate) {
          const parts = rawDate?.split('-');
          if (parts?.length === 3) {
            formattedDate = `${parts[2]}-${parts[1]}-${parts[0]}`;
          } else {
            formattedDate = rawDate;
          }
        }

        const label = rawDate
          ? `${partnerName} - ${formattedDate}`
          : `${partnerName} - No Effective Date`;

        return {
          label,
          value: contract?.contract_id,
          contractData: contract,
        };
      });

      setCurrentAgreementOptions(options);
    } catch (error: any) {
      setCurrentAgreementOptions([]);
      openSnackbar('Failed to load existing agreements', 'error', 3000);
    }
  };

  const addRenewalProcess = async (payload: any) => {
    try {
      const response = await axios.post(
        properties?.aiqBaseUrl +
          properties?.renewals +
          properties?.addNewRenewal,
        payload,
      );
      return response?.data;
    } catch (error: any) {
      throw error?.response?.data || error?.message;
    }
  };

  const fetchRenewals = async () => {
    if (!eg_nid) {
      setContracts([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const response = await axios?.get(
        properties?.aiqBaseUrl +
          properties?.renewals +
          properties?.getNewRenewal,
        {
          params: {
            eg_nid,
            month: selectedRenewalPeriod?.split(' ')[0] || 'January',
            year:
              selectedRenewalPeriod?.split(' ')[1] ||
              (new Date().getFullYear() + 1).toString(),
            limit: 100,
          },
        },
      );
      const renewals = response?.data?.data || [];
      const formattedContracts = renewals?.map((item: any) => {
        const daysUntilRenewal = Math?.ceil(
          (new Date(item?.renewal_date)?.getTime() - new Date()?.getTime()) /
            (1000 * 60 * 60 * 24),
        );

        const expiringDays =
          daysUntilRenewal < 0
            ? 'Expired'
            : daysUntilRenewal === 0
            ? 'Expiring Today'
            : daysUntilRenewal === 1
            ? 'Expiring in 1 day'
            : `Expiring in ${daysUntilRenewal} days`;

        return {
          agreementName: item?.service_agreement_name || 'Unknown Agreement',
          renewalDate: formatDateToDDMMMMYYYY(item?.renewal_date),
          expectedDate: formatDateToDDMMMMYYYY(item?.expected_release_date),
          expiringDays,
          currentVendor: item?.health_care_partner_name || 'Unknown Vendor',
          ...item,
        };
      });

      setContracts(formattedContracts);
    } catch (error: any) {
      openSnackbar('Failed to load renewal data', 'error', 5000);
      setContracts([]);
    } finally {
      setLoading(false);
    }
  };

  const editRenewalProcess = async (id: string, updateData: any) => {
    try {
      const response = await axios.post(
        properties?.aiqBaseUrl + properties?.renewals + '/update-renewal',
        {
          id,
          updateData,
        },
      );
      fetchRenewals();
      openSnackbar('Agreement details updated successfully!', 'success', 3000);
      return response?.data;
    } catch (error: any) {
      throw error?.response?.data || error?.message;
    }
  };

  const deleteRenewalProcess = async (id: string) => {
    try {
      const response = await axios.post(
        properties?.aiqBaseUrl + properties?.renewals + '/delete-renewal',
        {
          id,
        },
      );
      return response?.data;
    } catch (error: any) {
      throw error?.response?.data || error?.message;
    }
  };

  //UseEffects
  useEffect(() => {
    if (!eg_nid) {
      return;
    }
    getDefaultRenewalMonth();
    fetchServiceAgreements();
  }, [eg_nid]);

  useEffect(() => {
    if (!eg_nid || !selectedRenewalPeriod) return;

    fetchRenewals();
  }, [selectedRenewalPeriod, eg_nid]);

  useEffect(() => {
    setSelectedCurrentAgreement('');
    if (!selectedVendorType || !eg_nid) {
      return;
    }
    getContracts();
  }, [selectedVendorType, eg_nid]);

  return (
    <Grid container className={classes.root}>
      {mainLoading ? (
        <Loader />
      ) : (
        <Grid className={classes.titleAndSubtitle}>
          <PageTitleAndSubTitle
            title={
              <Grid className={classes.title}>
                <Typography
                  fontSize="18px"
                  fontType="semiBold"
                  textColor={patientAccessTheme.palette.text.highemphasis}
                >
                  {message.TITLE}
                </Typography>

                <DatePicker
                  selectedDate={selectedRenewalPeriod}
                  handleChange={(_, value) => {
                    setSelectedRenewalPeriod(value || '');
                  }}
                  placeholder="MMMM YYYY"
                  height="32px"
                  width="164px"
                  format="MMMM yyyy"
                  views={['year', 'month']}
                  openTo="month"
                  minDate="2000-01-01"
                  maxDate="2035-12-31"
                  readOnly
                />
              </Grid>
            }
            subTitle={message.SUBTITLE}
            customButton={false}
            subTitleStyles={{ width: '1240px' }}
          />
          <Grid className={classes.hr} />
          <CardComponent
            title={
              <Grid className={classes.cardTitleContainer}>
                <Typography
                  fontSize="14px"
                  fontType="semiBold"
                  textColor={patientAccessTheme.palette.text.highemphasis}
                >
                  {message.CARD.TITLE}
                </Typography>
                {contracts.length > 0 && (
                  <Button
                    label={message.BUTTON.ADD_NEW_RENEWAL}
                    style={{
                      width: 'auto',
                      whiteSpace: 'nowrap',
                      textTransform: 'none',
                    }}
                    onClick={() => {
                      setSelectedVendorType('');
                      setSelectedCurrentAgreement('');
                      setSelectedContractId('');
                      setRenewalDate('');
                      setExpectedReleaseDate('');
                      setOpen(true);
                    }}
                  />
                )}
              </Grid>
            }
            body={
              <Grid className={classes.cardBody}>
                {loading ? (
                  <Loader height="300px" />
                ) : contracts?.length > 0 ? (
                  <>
                    <Grid className={classes.scrollableCardsContainer}>
                      <Grid className={classes.cardsGrid}>
                        {contracts?.map((contract, index) => (
                          <ContractRenewalCard
                            key={index}
                            agreementName={contract?.agreementName}
                            renewalDate={contract?.renewalDate}
                            expectedDate={contract?.expectedDate}
                            expiringDays={contract?.expiringDays}
                            currentVendor={contract?.currentVendor}
                            handleStartRenewal={() =>
                              handleStartRenewal(contract)
                            }
                            btnLabel={message?.BUTTON?.SUBMIT_BTN}
                            menuProps={{
                              menuItems: menuItems,
                              handleMenuItemClick: handleMenuItemClick,
                              data: contract,
                            }}
                          />
                        ))}
                      </Grid>
                    </Grid>
                  </>
                ) : (
                  <Grid className={classes?.noDataContainer}>
                    <NoAnnualData />
                    <Typography
                      fontSize="16px"
                      fontType="semiBold"
                      textColor={patientAccessTheme?.palette.text.highemphasis}
                      margin="24px 0px 20px 0px"
                    >
                      {message.CARD.NODATA}
                    </Typography>
                    <Button
                      label="Add New Agreement Type"
                      style={{
                        width: 'auto',
                        whiteSpace: 'nowrap',
                        textTransform: 'none',
                      }}
                      onClick={() => {
                        setSelectedVendorType('');
                        setSelectedCurrentAgreement('');
                        setSelectedContractId('');
                        setRenewalDate('');
                        setExpectedReleaseDate('');
                        setOpen(true);
                      }}
                    />
                  </Grid>
                )}
              </Grid>
            }
          />
        </Grid>
      )}
      <PopUpAddNewAgreement
        open={open}
        onClose={setOpen}
        vendorTypes={vendorTypes}
        currentAgreementOptions={currentAgreementOptions}
        selectedContractId={selectedContractId}
        setSelectedContractId={setSelectedContractId}
        selectedVendorType={selectedVendorType}
        setSelectedVendorType={setSelectedVendorType}
        selectedCurrentAgreement={selectedCurrentAgreement}
        setSelectedCurrentAgreement={setSelectedCurrentAgreement}
        renewalDate={renewalDate}
        setRenewalDate={setRenewalDate}
        expectedReleaseDate={expectedReleaseDate}
        setExpectedReleaseDate={setExpectedReleaseDate}
        onConfirm={handleSubmit}
        isAgreementDisabled={isEdit}
      />
      {isDeleteModalOpen && (
        <ConfirmationPopup
          icon={<DeleteBadge width="38px" height="38px" />}
          title={'Delete Renewal Process'}
          subTitle={
            'On confirmation, it will be deleted permanently. Are you sure?'
          }
          handleCancel={() => setIsDeleteModalOpen(false)}
          handleConfirm={handleDeleteRenewal}
          buttonsType="error"
        />
      )}
      <Snackbar
        {...snackbarState}
        handleClose={handleCloseSnackBar}
        vertical="bottom"
        horizontal="right"
      />
    </Grid>
  );
});

export default AnnualRenewalProcess;
